import { Pills } from '@amelio/ui-library';

import { selectStrengths } from './strengths';
import { SummarySection } from './SummarySection';

import type { Strength } from './strengths';

import { GROWTH_PANEL_SURFACE } from '@/components/CareerGrowth/careerGrowthStyles';
import { useAmelioTranslations } from '@/hooks';
import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';
import { getLocalizedText } from '@/utils/localization';

/** "Vous excellez dans…": the competencies of the employee's current profile they master. */
export const StrengthsSection = () => {
    const { t } = useAmelioTranslations();
    const evolution = usePortalEvolution();

    return (
        <SummarySection
            icon={'support-star'}
            title={t('employeePortalSummary.strengths.title')}
            evolution={evolution}
            dataTestId={'employee-portal-strengths'}
        >
            {(data) => (
                <StrengthList
                    strengths={selectStrengths(data)}
                    hasProfile={data.currentProfile !== null}
                />
            )}
        </SummarySection>
    );
};

interface StrengthListProps {
    strengths: Strength[];
    /**
     * Whether a competency profile is assigned at all. Without one, no assessment will ever fill
     * this section: saying to wait for one would name a cause that is not the reason.
     */
    hasProfile: boolean;
}

const StrengthList = ({ strengths, hasProfile }: StrengthListProps) => {
    const { t } = useAmelioTranslations();

    if (strengths.length === 0) {
        return (
            <p className={`${GROWTH_PANEL_SURFACE} text-growth-slate px-6 py-6 text-[14px]`}>
                {hasProfile
                    ? t('employeePortalSummary.strengths.empty')
                    : t('employeePortalSummary.noCurrentProfile')}
            </p>
        );
    }

    return (
        <ul className={'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3'}>
            {strengths.map(({ skill, level }) => (
                <li
                    key={skill.competencyId}
                    className={`${GROWTH_PANEL_SURFACE} flex flex-col items-start justify-between gap-6 p-4 sm:gap-12 sm:p-6`}
                >
                    <div className={'flex flex-col gap-2'}>
                        <h3 className={'text-navy text-[20px] leading-tight font-medium'}>
                            {getLocalizedText(skill.name)}
                        </h3>
                        <p className={'text-growth-slate text-[13px]'}>
                            {getLocalizedText(skill.description)}
                        </p>
                    </div>
                    <Pills
                        variant={'primary'}
                        size={'sm'}
                    >
                        {getLocalizedText(level.label)}
                    </Pills>
                </li>
            ))}
        </ul>
    );
};
